import { useRsrc } from '@/common/rsrc/RsrcContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

// 1) Hook to track the user’s selection range
function useSelection(inputRef) {
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const updateSelection = () => {
    const el = inputRef.current;
    if (el) {
      setSelection({
        start: el.selectionStart,
        end: el.selectionEnd,
      });
    }
  };
  return [selection, updateSelection];
}

// 2) Hook to insert text at the current selection and restore the caret
function useInserter(formula, setFormula, selection, inputRef) {
  return (text) => {
    const { start, end } = selection;
    const before = formula.slice(0, start);
    const after = formula.slice(end);
    const nextPos = start + text.length;
    setFormula(before + text + after);
    window.requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(nextPos, nextPos);
      }
    });
  };
}

export function ProgProdFormulaDialog({ data, closeDialog }) {
  const rsrc = useRsrc();
  const [selData, setSelData] = useState(null);

  const [formula, setFormula] = useState(data.formula);
  const inputRef = useRef(null);
  const [selection, updateSelection] = useSelection(inputRef);
  const insertAtCursor = useInserter(formula, setFormula, selection, inputRef);

  const [priceType, setPriceType] = useState(null);
  const modifiers = {
    BOQ: 'Beginning of Quarter',
    EOQ: 'End of Quarter',
  };
  const [modifier, setModifier] = useState(null);
  const [lag, setLag] = useState(0);

  useEffect(() => {
    rsrc.data('md', 'FORMULA_DIALOG_DATA_PLUGIN').then((res) => {
      setSelData(res);
    });
  }, []);

  const onPriceTypeChange = (event) => {
    setModifier(null);
    setPriceType(event.target.value);
  };

  const handleInsertPrice = () => {
    const comp = (lag ? 'LAG' + lag + '_' : '') + (modifier || '') + (priceType || '');
    insertAtCursor(comp);
  };

  const handleInsertOper = (oper) => {
    insertAtCursor(oper.tmpl);
  };

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={() => closeDialog()}>
      <DialogTitle>Formula Builder</DialogTitle>
      <DialogContent>
        <TextField
          variant="outlined"
          size="medium"
          label="Formula Expression"
          fullWidth
          value={formula}
          onChange={(e) => {
            setFormula(e.target.value);
            updateSelection();
          }}
          onKeyUp={updateSelection}
          onMouseUp={updateSelection}
          inputRef={inputRef}
          slotProps={{
            input: {
              onSelect: updateSelection,
            },
          }}
          sx={{
            mt: 2,
            mb: 2,
            // make the label bold and in your primary color
            '& .MuiInputLabel-root': {
              fontWeight: 'bold',
              color: 'primary.main',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'primary.dark',
            },
            // enlarge the input text and thicken the border
            '& .MuiOutlinedInput-root': {
              fontSize: '1.1rem',
              '& fieldset': {
                borderWidth: 2,
              },
              '&:hover fieldset': {
                borderColor: 'primary.dark',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                boxShadow: (theme) => `0 0 0 4px ${theme.palette.primary.light}33`,
              },
            },
            // optional: give the whole field a slight background tint
            backgroundColor: (theme) => theme.palette.primary.light + '08',
            borderRadius: 1,
          }}
        />

        <Box display="flex" gap={2} alignItems="flex-start">
          {/* Left column */}
          <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap={2}>
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                p: 2,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography variant="subtitle1">Price Component Builder</Typography>

              <FormControl fullWidth>
                <InputLabel id="pt-label">Price Type</InputLabel>
                <Select
                  labelId="pt-label"
                  label="Price Type"
                  value={priceType || ''}
                  onChange={(event) => onPriceTypeChange(event)}
                >
                  {selData &&
                    Object.entries(selData.priceTypes).map(([name, displayName]) => (
                      <MenuItem key={name} value={name}>
                        {displayName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {priceType && !selData.qtrPriceTypes.includes(priceType) && (
                <FormControl fullWidth>
                  <InputLabel id="mod-label">Modifier</InputLabel>
                  <Select
                    labelId="mod-label"
                    label="Modifier"
                    value={modifier || ''}
                    onChange={(e) => setModifier(e.target.value)}
                  >
                    {Object.entries(modifiers).map(([name, displayName]) => (
                      <MenuItem key={name} value={name}>
                        {displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                type="number"
                label="Quarterly Lag"
                value={lag}
                onChange={(e) => setLag(Number(e.target.value))}
              />

              <Button variant="contained" onClick={handleInsertPrice}>
                Insert
              </Button>
            </Paper>
          </Box>

          {/* Right column */}
          <Box flex={1} minWidth={0}>
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                p: 2,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Available Operators
              </Typography>

              {selData && <ScrollableList items={selData.opers} onItemClick={handleInsertOper} height={300} />}
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => closeDialog(data.formula)}>Cancel</Button>
        <Button onClick={() => closeDialog(formula)}>Submit</Button>
      </DialogActions>
    </Dialog>
  );
}

export function ScrollableList({ items, onItemClick, height = 200 }) {
  return (
    <div
      style={{
        maxHeight: `${height}px`,
        overflowY: 'auto',
        border: '1px solid #ccc',
        borderRadius: 4,
        padding: 8,
      }}
    >
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {items.map((item) => (
          <li
            key={item.code}
            onClick={() => onItemClick(item)}
            style={{
              padding: 8,
              cursor: 'pointer',
              borderBottom: '1px solid #eee',
            }}
          >
            {item.tmpl}
          </li>
        ))}
      </ul>
    </div>
  );
}
